import * as React from 'react';
import { Grid2 as Grid, Card, CardContent } from '@mui/material';
import { ActionRequest } from '../../actions';
import { Loading, Table, BarChart } from '../common';
import { getFlagIcon, truncateNumber } from '../../utils';

interface Props {
    readonly styled: StyledProps;
}

interface State {
    readonly loading: boolean;
    readonly data?: AnalyticsModels.Last30MinutesRealTimeReport;
}

export class Last30MinutesRealTimeReport extends React.PureComponent<Props, State> {
    state: State = {
        loading: true,
    };

    async componentDidMount() {
        await this.getData();
    }

    render() {
        const { styled } = this.props;
        const { loading, data } = this.state;
        if (loading) {
            return (
                <Card style={{ padding: 24 }}>
                    <div style={{ height: 362 }}>
                        <Loading styled={styled} />
                    </div>
                </Card>
            );
        }

        return (
            <Card style={{ padding: 10 }}>
                <CardContent>
                    {!data && (
                        <div style={{ textAlign: 'center' }}>
                            No data available. Please try again later.
                        </div>
                    )}
                    {data && (
                        <Grid container={true} justifyContent="center" spacing={1}>
                            <Grid size={12}>
                                <div style={{ color: styled.themeMode === 'light' ? '#5f6368' : '#8a8e93', letterSpacing: '.5px', fontSize: 14, fontWeight: 500, textUnderlinePosition: 'under', textUnderlineOffset: 1, textDecorationLine: 'underline', textDecorationStyle: 'dashed' }}>
                                    ACTIVE USERS IN LAST 30 MINUTES
                                </div>
                            </Grid>
                            <Grid size={12}>
                                <div style={{ fontWeight: 400, fontSize: 36 }}>
                                    {data.totalUsers.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            </Grid>
                            <Grid size={12}>
                                <div style={{ color: styled.themeMode === 'light' ? '#5f6368' : '#8a8e93', letterSpacing: '.5px', fontSize: 14, fontWeight: 500, textUnderlinePosition: 'under', textUnderlineOffset: 1, textDecorationLine: 'underline', textDecorationStyle: 'dashed' }}>
                                    ACTIVE USERS PER MINUTE
                                </div>
                                <BarChart
                                    styled={styled}
                                    height={150}
                                    series={[{
                                        data: data.last30Minutes.map(d => d.totalUsers),
                                        type: 'bar',
                                        color: '#1973e8',
                                    }]}
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: data.last30Minutes.map(d => `${d.minutesAgo} minutes ago`),
                                    }]}
                                    sx={{
                                        '& .MuiChartsAxis-tickLabel': {
                                            display: 'none',
                                        },
                                        '& .MuiChartsAxis-tick': {
                                            display: 'none',
                                        },
                                        '& .MuiChartsAxis-line': {
                                            stroke: styled.themeMode === 'light' ? '#7d8085' : '#a7aaae !important',
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Table
                                    styled={styled}
                                    columns={[
                                        {
                                            title: 'TOP COUNTRIES',
                                            field: 'country',
                                            render: (rowData: AnalyticsModels.CountryTotalUsersReport) => `${getFlagIcon(rowData.countryId)} ${rowData.country}`},
                                        {
                                            title: 'ACTIVE USERS',
                                            field: 'totalUsers',
                                            render: (rowData: AnalyticsModels.CountryTotalUsersReport) => `${truncateNumber(rowData.totalUsers)}`,
                                            cellStyle: {
                                                textAlign: 'right',
                                            },
                                            headerStyle: {
                                                textAlign: 'right',
                                            }
                                        },
                                    ]}
                                    data={data.countries}
                                    options={{
                                        selection: false,
                                        paging: false,
                                        emptyRowsWhenPaging: false,
                                        search: false,
                                        sorting: false,
                                        draggable: false,
                                        showTitle: false,
                                        toolbar: false,
                                    }}
                                    showBox={false}
                                />
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        );
    }

    private async getData(): Promise<void> {
        this.setState({
            loading: true,
            data: undefined,
        });
        const url = `analytics/last30MinutesRealTimeReport`;
        const data = await ActionRequest.get(url);
        this.setState({
            loading: false,
            data: data || undefined,
        });
    }
}